import { useMutation, useQuery } from '@tanstack/react-query';
import { FileSignature } from 'lucide-react';
import { fetchSignatures, openSignature } from '../api/candidate';
import { dayOf, fmtDayFull } from '../lib/dates';
import { Button, Card } from './charte';

const KINDS = { quote: 'Devis', order: 'Commande' };

/**
 * Devis et commandes que l'auto-école a envoyés en signature : l'élève connecté
 * les signe d'ici, sans retrouver le lien reçu par e-mail. Un code lui est
 * envoyé, que la page de signature vérifie.
 */
export default function PendingSignatures() {
  const { data } = useQuery({
    queryKey: ['candidate', 'signatures'],
    queryFn: fetchSignatures,
  });
  const open = useMutation({
    mutationFn: openSignature,
    onSuccess: (challenge) => {
      const access = new URLSearchParams({
        challenge: challenge.challengeId,
        email: challenge.email,
        kind: challenge.documentKind,
      });
      window.location.assign(`/signer#${access.toString()}`);
    },
  });

  const documents = data ?? [];
  if (documents.length === 0) return null;

  return (
    <Card className="ring-1 ring-primary-100">
      <h2 className="text-title-sm text-heading">À signer</h2>
      <p className="mt-1 text-sm text-secondary">
        Votre auto-école vous a envoyé{' '}
        {documents.length > 1 ? `${documents.length} documents` : 'un document'}{' '}
        à signer en ligne. Un code vous sera envoyé par e-mail pour confirmer
        votre identité.
      </p>
      <div className="mt-4 divide-y divide-slate-100">
        {documents.map((document) => (
          <div
            key={document.requestId}
            className="flex items-center gap-3 py-3.5 first:pt-0 last:pb-0"
          >
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-surface-indigo text-primary">
              <FileSignature size={18} aria-hidden />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-heading truncate">
                {KINDS[document.kind] ?? 'Document'}{' '}
                {document.documentName.replace(/\.pdf$/i, '')}
              </div>
              <div className="text-xs text-secondary">
                {document.signed
                  ? 'Votre signature est enregistrée. Une autre signature est attendue.'
                  : `À signer avant le ${fmtDayFull(dayOf(document.expiresAt))}`}
              </div>
            </div>
            {!document.signed && (
              <Button
                size="sm"
                variant="soft"
                onClick={() => open.mutate(document.requestId)}
                disabled={open.isPending}
                loading={open.isPending && open.variables === document.requestId}
              >
                Signer
              </Button>
            )}
          </div>
        ))}
      </div>
      {open.isError && (
        <p role="alert" className="mt-3 rounded-xl bg-danger-light px-3 py-2 text-xs text-danger-dark">
          {open.error instanceof Error && open.error.message
            ? open.error.message
            : 'Envoi du code impossible. Réessayez dans une minute.'}
        </p>
      )}
    </Card>
  );
}
